import { ref, computed } from 'vue';
import marketFlowService from '@/service/marketFlowService';
import {
  GroupType,
  getRowKey,
  getColKey,
  passMarketFilter,
  toEok,
  maturityBucketOrder,
  creditTypeOrder,
  institutionTypeOrder,
} from '@/utils/marketFlowUtil';

/**
 * Market Flow 데이터 및 필터 관리 Composable
 */
export function useMarketFlow() {
  // 원본 데이터
  const allRecords = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // UI 상태
  const groupType = ref(GroupType.BOND_TYPE);
  const selectedCreditType = ref(null);
  const selectedInstitution = ref(null);
  const selectedBondGroup = ref(null); // 채권 그룹 선택

  // 로컬스토리지에서 저장된 필터 상태 불러오기
  const savedDirection = localStorage.getItem('marketFlow_direction');
  const savedMarket = localStorage.getItem('marketFlow_market');

  // 필터 상태
  const direction = ref(
    savedDirection
      ? JSON.parse(savedDirection)
      : {
          buy: true,
          sell: true,
          net: true,
        }
  );

  const market = ref(
    savedMarket
      ? JSON.parse(savedMarket)
      : {
          primary: true,
          secondary: true,
        }
  );

  // 선택된 셀 정보
  const selectedCell = ref({
    rowKey: null,
    colKey: null,
  });

  /**
   * 데이터 조회
   */
  const fetchRecords = async date => {
    loading.value = true;
    error.value = null;
    try {
      const response = await marketFlowService.getRecords(date);
      // axios 인터셉터가 이미 response.data.data를 반환함
      allRecords.value = response || [];
      console.log('📊 Market Flow 데이터 조회:', {
        totalRecords: allRecords.value.length,
        sampleRecord: allRecords.value[0],
        date,
      });
    } catch (err) {
      console.error('수급 데이터 조회 실패:', err);
      error.value = err;
      allRecords.value = [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * 대시보드용 레코드 필터링
   * 방향 필터(매도/매수/순매수)는 표시 방식만 결정하므로 레코드 필터링에서 제외
   */
  const filterRecordsForDashboard = records => {
    return records.filter(r => {
      // 1) status 필터 (DETERMINED만 사용)
      if (r.status !== 'DETERMINED') return false;

      // 2) 분류 그룹별 선택값 필터
      if (groupType.value === GroupType.BOND_TYPE) {
        // 채권 유형 그룹일 때 => 선택된 creditType이 있으면 필터
        if (
          selectedCreditType.value &&
          r.issuer.creditType !== selectedCreditType.value
        ) {
          return false;
        }

        // 채권 그룹 필터 (issuerIds 기반)
        if (
          selectedBondGroup.value &&
          selectedBondGroup.value.issuerIds &&
          selectedBondGroup.value.issuerIds.length > 0
        ) {
          if (!selectedBondGroup.value.issuerIds.includes(r.issuer.id)) {
            return false;
          }
        }
      } else {
        // 기관 유형 그룹일 때 => 선택된 institutionType이 있으면 필터
        if (
          selectedInstitution.value &&
          r.institutionType !== selectedInstitution.value
        ) {
          return false;
        }
      }

      // 3) 방향 필터는 제외 - 수급 현황 테이블의 표시 방식만 결정

      // 4) 시장 필터
      if (!passMarketFilter(r, market.value)) return false;

      return true;
    });
  };

  /**
   * 필터링된 레코드 (computed)
   */
  const filteredRecords = computed(() => {
    const filtered = filterRecordsForDashboard(allRecords.value);
    console.log('🔍 필터링된 레코드:', {
      total: allRecords.value.length,
      filtered: filtered.length,
      groupType: groupType.value,
      direction: direction.value,
      market: market.value,
    });
    return filtered;
  });

  /**
   * 수급 현황 테이블 집계
   */
  const buildSummary = (records, gType) => {
    const map = new Map();

    for (const r of records) {
      const rowKey = getRowKey(r, gType);
      const colKey = getColKey(r);
      const key = `${rowKey}__${colKey}`;

      const existing = map.get(key) || {
        rowKey,
        colKey,
        buyAmount: 0,
        sellAmount: 0,
      };

      if (r.flowType === 'IN') {
        existing.buyAmount += r.amount;
      } else {
        existing.sellAmount += r.amount;
      }

      map.set(key, existing);
    }

    console.log('📈 집계 결과:', {
      totalCells: map.size,
      cells: Array.from(map.entries()).slice(0, 3),
    });

    return map;
  };

  /**
   * 수급 현황 테이블 데이터 (computed)
   */
  const summaryTableData = computed(() => {
    const summary = buildSummary(filteredRecords.value, groupType.value);
    const rows = [];

    // 행 목록 결정
    const rowKeys =
      groupType.value === GroupType.BOND_TYPE
        ? institutionTypeOrder
        : creditTypeOrder;

    for (const rowKey of rowKeys) {
      const row = { rowKey, cells: {} };

      for (const colKey of maturityBucketOrder) {
        const key = `${rowKey}__${colKey}`;
        const cell = summary.get(key);

        if (cell) {
          // 셀에 매수/매도 금액 모두 포함
          row.cells[colKey] = {
            buyAmount: toEok(cell.buyAmount),
            sellAmount: toEok(cell.sellAmount),
            netAmount: toEok(cell.buyAmount - cell.sellAmount),
          };
        } else {
          row.cells[colKey] = {
            buyAmount: 0,
            sellAmount: 0,
            netAmount: 0,
          };
        }
      }

      rows.push(row);
    }

    return rows;
  });

  /**
   * 상세 테이블용 레코드 필터링
   * 선택된 셀의 모든 거래를 표시 (매수/매도 모두 포함)
   */
  const filterRecordsForDetail = () => {
    const { rowKey, colKey } = selectedCell.value;
    if (!rowKey || !colKey) return [];

    // 1단계: 선택된 셀에 해당하는 레코드들의 code 수집
    const relevantCodes = new Set();
    
    allRecords.value.forEach(r => {
      if (r.status !== 'DETERMINED') return;
      
      // 섹터별 분류: 선택된 creditType 필터 적용
      if (groupType.value === GroupType.BOND_TYPE) {
        if (selectedCreditType.value && r.issuer.creditType !== selectedCreditType.value) {
          return;
        }
        // 채권 그룹 필터
        if (
          selectedBondGroup.value &&
          selectedBondGroup.value.issuerIds &&
          selectedBondGroup.value.issuerIds.length > 0
        ) {
          if (!selectedBondGroup.value.issuerIds.includes(r.issuer.id)) {
            return;
          }
        }
      }
      
      // 기관별 분류: 선택된 institutionType 필터 적용
      if (groupType.value === GroupType.INSTITUTION_TYPE) {
        if (selectedInstitution.value && r.institutionType !== selectedInstitution.value) {
          return;
        }
      }
      
      const rRowKey = getRowKey(r, groupType.value);
      const rColKey = getColKey(r);
      
      // 선택된 셀과 일치하는 레코드의 code 수집
      if (rRowKey === rowKey && rColKey === colKey) {
        relevantCodes.add(r.code);
      }
    });

    // 2단계: 수집된 code를 가진 모든 거래 반환 (매수/매도 양쪽 모두)
    // 단, 동일한 섹터(creditType) 필터는 유지
    return allRecords.value.filter(r => {
      // 1) status 필터
      if (r.status !== 'DETERMINED') return false;

      // 2) 섹터 필터 유지 (섹터별/기관별 모두)
      if (groupType.value === GroupType.BOND_TYPE) {
        if (selectedCreditType.value && r.issuer.creditType !== selectedCreditType.value) {
          return false;
        }
        if (
          selectedBondGroup.value &&
          selectedBondGroup.value.issuerIds &&
          selectedBondGroup.value.issuerIds.length > 0
        ) {
          if (!selectedBondGroup.value.issuerIds.includes(r.issuer.id)) {
            return false;
          }
        }
      }
      
      // 3) 해당 code를 가진 모든 레코드 포함
      if (!relevantCodes.has(r.code)) return false;

      // 4) 시장 필터 적용
      if (!passMarketFilter(r, market.value)) return false;

      // 5) 만기 버킷 확인
      const rColKey = getColKey(r);
      if (rColKey !== colKey) return false;

      return true;
    });
  };

  /**
   * 상세 테이블 데이터 (computed)
   */
  const detailTableData = computed(() => {
    return filterRecordsForDetail();
  });

  /**
   * 셀 선택
   */
  const selectCell = (rowKey, colKey) => {
    selectedCell.value = { rowKey, colKey };
  };

  /**
   * 셀 선택 해제
   */
  const clearCellSelection = () => {
    selectedCell.value = { rowKey: null, colKey: null };
  };

  /**
   * 그룹 타입 변경
   */
  const changeGroupType = newGroupType => {
    groupType.value = newGroupType;
    // 그룹 변경 시 셀 선택 해제
    clearCellSelection();
  };

  /**
   * 채권 유형 선택 변경
   */
  const changeCreditType = newCreditType => {
    selectedCreditType.value = newCreditType;
    selectedBondGroup.value = null; // 채권 유형 변경 시 그룹 초기화
    clearCellSelection();
  };

  /**
   * 채권 그룹 선택 변경
   */
  const changeBondGroup = newGroup => {
    selectedBondGroup.value = newGroup;
    // 그룹은 단순 필터링이므로 셀 선택 유지
  };

  /**
   * 기관 유형 선택 변경
   */
  const changeInstitution = newInstitution => {
    selectedInstitution.value = newInstitution;
    clearCellSelection();
  };

  /**
   * 방향 필터 변경
   */
  const updateDirectionFilter = newDirection => {
    direction.value = { ...direction.value, ...newDirection };
    // 로컬스토리지에 저장
    localStorage.setItem('marketFlow_direction', JSON.stringify(direction.value));
    // 필터 변경 시 셀 선택 유지 (자동 재계산됨)
  };

  /**
   * 시장 필터 변경
   */
  const updateMarketFilter = newMarket => {
    market.value = { ...market.value, ...newMarket };
    // 로컬스토리지에 저장
    localStorage.setItem('marketFlow_market', JSON.stringify(market.value));
    // 필터 변경 시 셀 선택 유지 (자동 재계산됨)
  };

  return {
    // 데이터
    allRecords,
    loading,
    error,

    // 상태
    groupType,
    selectedCreditType,
    selectedInstitution,
    selectedBondGroup,
    direction,
    market,
    selectedCell,

    // Computed
    filteredRecords,
    summaryTableData,
    detailTableData,

    // 메서드
    fetchRecords,
    selectCell,
    clearCellSelection,
    changeGroupType,
    changeCreditType,
    changeInstitution,
    changeBondGroup,
    updateDirectionFilter,
    updateMarketFilter,
  };
}

